/**
 * HR Org Structure admin — /hr/org-structure.
 *
 * The single home for organizational structure, as a two-tab view:
 *   • Units — CRUD the org-unit tree (create/rename/move/set-head/delete)
 *   • Teams — full team CRUD + org-unit assignment (embeds HrTeamsPageInner)
 *
 * Kept off the (read-only) org chart deliberately — the chart is for
 * viewing. Backed by /api/v1/hr/org-units and /api/v1/admin/teams. Gated on
 * `flags.orgStructureAdmin`.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { OrgUnitType } from '@lms/shared-types'
import { PageHead } from '../components/primitives/PageHead'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { Combobox } from '../components/primitives/Combobox'
import { RowActionsMenu, type RowAction } from '../components/primitives/RowActionsMenu'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useToast } from '../components/primitives/Toast'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { ApiError } from '../api/client'
import { getOrgChart } from '../api/hr-org-chart'
import { createOrgUnit, deleteOrgUnit } from '../api/org-units'
import {
  UnitEditorModal,
  type EmpLite,
  type UnitEditorTarget,
  type UnitLite,
} from '../components/hr/org-chart/v2/OrgStructureEditors'
import { UnitTypeChip } from '../components/hr/org-chart/UnitTypeChip'
import { HrTeamsPageInner } from './HrTeamsPage'
import { ApproversTab } from '../components/hr/approvers/ApproversTab'
import { flags } from '../lib/feature-flags'

const UNIT_TYPE_LABEL: Record<OrgUnitType, string> = {
  company: 'Company',
  function: 'Function',
  department: 'Department',
}

const UNIT_TYPES: { value: OrgUnitType; label: string }[] = [
  { value: 'company', label: 'Company' },
  { value: 'function', label: 'Function' },
  { value: 'department', label: 'Department' },
]

const fieldStyle: CSSProperties = {
  padding: '8px 10px',
  fontSize: 14,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
}

// No-line tabs: the active tab is indicated by colour + weight only — no
// underline, no baseline, no focus ring. Nothing lingers under a clicked tab.
const tabListStyle: CSSProperties = {
  display: 'flex',
  gap: 18,
  marginBottom: 20,
}
const tabButtonStyle: CSSProperties = {
  padding: '4px 0 6px',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: 'inherit',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  outline: 'none',
  boxShadow: 'none',
  cursor: 'pointer',
  color: 'var(--text-3)',
  transition: 'color 120ms ease, border-color 120ms ease',
}
const tabButtonActiveStyle: CSSProperties = {
  color: 'var(--steel-blue)',
  fontWeight: 700,
  // Toggle the SAME `borderBottom` shorthand the base sets (not the
  // border-bottom-color longhand) — mixing shorthand + longhand makes
  // React's style diff leave the colour stale on the deselected tab.
  borderBottom: '2px solid var(--steel-blue)',
}

// ── page ──────────────────────────────────────────────────────────────────────

export function HrOrgStructurePage(): JSX.Element | null {
  if (!flags.orgStructureAdmin) return null
  return (
    <HrAccessGuard>
      <div className="page">
        <HrOrgStructureInner />
      </div>
    </HrAccessGuard>
  )
}

type Tab = 'units' | 'teams' | 'approvers'

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

// Inline create surface — the same shape as Teams' CreateTeamRow: a Card of
// fields above the table, no modal. Self-contained (owns its own mutation +
// state). `parentPreset` lets the "Add sub-unit" row action seed the parent
// and focus the name field; it's a fresh token each click so re-selecting the
// same parent still re-triggers.
function CreateUnitRow({
  units,
  employees,
  parentPreset,
}: {
  units: UnitLite[]
  employees: EmpLite[]
  parentPreset: { id: string; token: number } | null
}): JSX.Element {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [name, setName] = useState('')
  const [unitType, setUnitType] = useState<OrgUnitType>('department')
  const [parentId, setParentId] = useState('')
  const [headId, setHeadId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  // "Add sub-unit" from a row → prefill the parent and jump to the name field.
  useEffect(() => {
    if (!parentPreset) return
    setParentId(parentPreset.id)
    nameRef.current?.focus()
  }, [parentPreset])

  const mutation = useMutation({
    mutationFn: () =>
      createOrgUnit({
        parentId: parentId || null,
        unitType,
        name: name.trim(),
        headEmployeeId: headId || null,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify(`Created unit “${created.name}”`, 'success')
      setName('')
      setUnitType('department')
      setParentId('')
      setHeadId('')
      setError(null)
    },
    onError: (err: unknown) => {
      setError(apiErrorMessage(err, 'Failed to create unit'))
    },
  })

  function submit(): void {
    setError(null)
    if (name.trim().length === 0) {
      setError('Unit name is required.')
      return
    }
    // Parent is optional: a parent-less unit becomes the org-tree root. The
    // api enforces the single-root rule (409 if a root already exists), so we
    // let it decide rather than blocking here.
    mutation.mutate()
  }

  return (
    <Card padding={16} style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={nameRef}
          data-testid="unit-create-input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              submit()
            }
          }}
          placeholder="New unit name"
          aria-label="New unit name"
          maxLength={120}
          style={{
            ...fieldStyle,
            flex: '1 1 220px',
            minWidth: 180,
            border: error ? '1.5px solid var(--danger-line)' : fieldStyle.border,
          }}
        />
        <select
          data-testid="unit-create-type"
          aria-label="Unit type"
          value={unitType}
          onChange={(e) => setUnitType(e.target.value as OrgUnitType)}
          style={{ ...fieldStyle, flex: '0 1 150px' }}
        >
          {UNIT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <Combobox
          inputTestId="unit-create-parent"
          ariaLabel="Parent unit (optional)"
          placeholder="Parent unit (optional)"
          emptyOptionLabel="— No parent (root) —"
          value={parentId}
          onChange={setParentId}
          options={units.map((u) => ({
            value: u.id,
            label: u.name,
            badge: <UnitTypeChip unitType={u.unitType} />,
          }))}
          style={{ flex: '0 1 200px' }}
        />
        <Combobox
          inputTestId="unit-create-head"
          ariaLabel="Head"
          placeholder="Head (optional)"
          emptyOptionLabel="— No head —"
          value={headId}
          onChange={setHeadId}
          options={employees.map((e) => ({
            value: e.id,
            label: e.name,
            sublabel: e.jobTitle,
          }))}
          style={{ flex: '0 1 200px' }}
        />
        <Button
          variant="primary"
          type="button"
          onClick={submit}
          loading={mutation.isPending}
          data-testid="unit-create-submit"
        >
          + Add unit
        </Button>
      </div>
      {error ? (
        <p
          role="alert"
          data-testid="unit-create-error"
          style={{ fontSize: 12, color: 'var(--danger-fg)', margin: '8px 0 0' }}
        >
          {error}
        </p>
      ) : null}
    </Card>
  )
}

function HrOrgStructureInner(): JSX.Element {
  const [tab, setTab] = useState<Tab>('units')
  // Edit still uses the modal; create is the inline CreateUnitRow below.
  const [editTarget, setEditTarget] = useState<UnitEditorTarget | null>(null)
  // Bumped each time "Add sub-unit" is clicked so the inline row re-seeds even
  // for the same parent.
  const [parentPreset, setParentPreset] = useState<{
    id: string
    token: number
  } | null>(null)
  const presetTokenRef = useRef(0)
  const { notify } = useToast()
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['hr', 'org-chart'],
    queryFn: ({ signal }) => getOrgChart(signal),
    staleTime: 5 * 60_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (unitId: string) => deleteOrgUnit(unitId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] })
      await queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify('Unit deleted', 'success')
    },
    onError: (err: unknown) => {
      notify(`Couldn't delete — ${apiErrorMessage(err, 'failed')}`, 'error')
    },
  })

  function handleDelete(unit: UnitLite): void {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the “${unit.name}” unit?`)) return
    deleteMutation.mutate(unit.id)
  }

  const orgUnits = query.data?.orgUnits ?? []
  const teams = query.data?.teams ?? []
  const employees = query.data?.employees ?? []

  const unitNameById = useMemo(
    () => new Map(orgUnits.map((u) => [u.id, u.name])),
    [orgUnits],
  )
  const empNameById = useMemo(
    () => new Map(employees.map((e) => [e.id, e.name])),
    [employees],
  )
  const unitLites = useMemo<UnitLite[]>(
    () =>
      [...orgUnits]
        .map((u) => ({ id: u.id, name: u.name, unitType: u.unitType }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgUnits],
  )
  const empLites = useMemo<EmpLite[]>(
    () =>
      [...employees]
        .map((e) => ({ id: e.id, name: e.name, jobTitle: e.jobTitle }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [employees],
  )
  const teamCountByUnit = useMemo(() => {
    const m = new Map<string, number>()
    for (const t of teams) {
      if (t.parentUnitId) m.set(t.parentUnitId, (m.get(t.parentUnitId) ?? 0) + 1)
    }
    return m
  }, [teams])
  const childCountByUnit = useMemo(() => {
    const m = new Map<string, number>()
    for (const u of orgUnits) {
      if (u.parentId) m.set(u.parentId, (m.get(u.parentId) ?? 0) + 1)
    }
    return m
  }, [orgUnits])
  const unitsSorted = useMemo(
    () => [...orgUnits].sort((a, b) => a.name.localeCompare(b.name)),
    [orgUnits],
  )

  // A unit can only be deleted when empty (no child units, no teams) — the
  // API enforces this with a 409. Mirror that here: disable Delete and say
  // exactly what's blocking it.
  function deleteAction(u: {
    id: string
    name: string
    unitType: OrgUnitType
  }): RowAction {
    const childN = childCountByUnit.get(u.id) ?? 0
    const teamN = teamCountByUnit.get(u.id) ?? 0
    const parts: string[] = []
    if (childN > 0) parts.push(`${childN} sub-unit${childN === 1 ? '' : 's'}`)
    if (teamN > 0) parts.push(`${teamN} team${teamN === 1 ? '' : 's'}`)
    const blocked = parts.length > 0
    const deleting =
      deleteMutation.isPending && deleteMutation.variables === u.id
    return {
      icon: 'delete',
      label: 'Delete',
      testId: `org-structure-delete-${u.id}`,
      destructive: true,
      disabled: blocked || deleting,
      ...(blocked ? { hint: `Move its ${parts.join(' and ')} out first` } : {}),
      onClick: () => handleDelete(u),
    }
  }

  const TABS: { id: Tab; label: string }[] = [
    { id: 'units', label: 'Units' },
    { id: 'teams', label: 'Teams' },
    { id: 'approvers', label: 'Approvers' },
  ]

  return (
    <>
      <PageHead
        eyebrow="HR · Configuration"
        title="Org structure"
        subtitle="Manage the org-unit tree, the teams under it, and who approves whose leave. Changes are reflected on the org chart."
      />

      <div role="tablist" aria-label="Org structure tabs" style={tabListStyle}>
        {TABS.map((t) => {
          const active = t.id === tab
          return (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={active}
              data-testid={`org-structure-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{ ...tabButtonStyle, ...(active ? tabButtonActiveStyle : {}) }}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'teams' ? (
        <HrTeamsPageInner embedded />
      ) : tab === 'approvers' ? (
        <ApproversTab />
      ) : (
        <>
          <CreateUnitRow
            units={unitLites}
            employees={empLites}
            parentPreset={parentPreset}
          />

          {query.isLoading ? (
            <div className="card" style={{ padding: '24px 18px' }}>
              <ThinkingDots label="Loading org structure" />
            </div>
          ) : query.isError ? (
            <div
              className="card"
              style={{ padding: '24px 18px', color: 'var(--danger-fg)', fontSize: 14 }}
            >
              Failed to load. Refresh the page to try again.
            </div>
          ) : (
            <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table
                className="tbl"
                data-testid="org-structure-units"
                style={{ minWidth: 640 }}
              >
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Type</th>
                    <th>Parent</th>
                    <th>Head</th>
                    <th>Teams</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {unitsSorted.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name}</td>
                      <td style={{ color: 'var(--text-2)' }}>
                        {UNIT_TYPE_LABEL[u.unitType]}
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>
                        {u.parentId ? (unitNameById.get(u.parentId) ?? '—') : '—'}
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>
                        {u.headEmployeeId
                          ? (empNameById.get(u.headEmployeeId) ?? '—')
                          : '—'}
                      </td>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {teamCountByUnit.get(u.id) ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <RowActionsMenu
                          ariaLabel={`Actions for ${u.name}`}
                          testId={`org-structure-actions-${u.id}`}
                          actions={[
                            {
                              icon: 'edit',
                              label: 'Edit',
                              testId: `org-structure-edit-${u.id}`,
                              onClick: () =>
                                setEditTarget({
                                  id: u.id,
                                  name: u.name,
                                  unitType: u.unitType,
                                  parentId: u.parentId,
                                  headId: u.headEmployeeId,
                                }),
                            },
                            {
                              icon: 'add',
                              label: 'Add sub-unit',
                              testId: `org-structure-addchild-${u.id}`,
                              onClick: () => {
                                presetTokenRef.current += 1
                                setParentPreset({
                                  id: u.id,
                                  token: presetTokenRef.current,
                                })
                              },
                            },
                            deleteAction(u),
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <UnitEditorModal
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        mode="edit"
        units={unitLites}
        employees={empLites}
        target={editTarget}
      />
    </>
  )
}
